import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../components/ui/Button';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';

import { useGetWallGroup, useSetGroupWalls } from '../../../../queries/wallGroups';

/**
 * Add/remove walls in a group. Shared by the Groups grid (Groups.jsx) and the
 * Group detail page (GroupShow.jsx). Geometry is enforced: the first wall sets the
 * group's layout and only matching walls can join (mismatch → body.status 422, the
 * dialog stays open so the user can fix the selection). Pure membership edit — no
 * RTDB, no sync impact.
 */
export default function GroupMembersDialog({ group, walls, onHide }) {
  const { t } = useTranslation();
  const { data: detail } = useGetWallGroup(group?.id, !!group);
  const setWalls = useSetGroupWalls();
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (detail) setSelected((detail.walls || []).map((w) => w.id));
  }, [detail]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const save = async () => {
    const res = await setWalls.mutateAsync({ id: group.id, wall_ids: selected });
    const body = res?.data?.data ?? res?.data;
    if (body?.status !== 422) onHide(); // keep open on geometry mismatch so the user can fix the selection
  };

  return (
    <Dialog visible={!!group} onHide={onHide} header={group ? t('video_wall.groups.manage_members_title', { name: group.name }) : ''} style={{ width: 'min(95vw, 520px)' }}
      footer={<div className="d-flex justify-content-end gap-2"><Button label={t('video_wall.groups.cancel')} text onClick={onHide} /><Button label={t('video_wall.groups.save')} icon="pi pi-check" loading={setWalls.isLoading} onClick={save} /></div>}>
      {detail?.rows ? (
        <p className="small mb-2" style={{ color: 'var(--pp-text-secondary)' }}>
          <i className="pi pi-info-circle me-1" />
          {t('video_wall.groups.geometry_req', { geom: `${detail.rows}×${detail.columns} ${detail.orientation}` })}
        </p>
      ) : (
        <p className="small mb-2" style={{ color: 'var(--pp-text-secondary)' }}>
          <i className="pi pi-info-circle me-1" />{t('video_wall.groups.geometry_set_hint')}
        </p>
      )}
      {walls.length === 0 ? (
        <p className="small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.groups.no_walls')}</p>
      ) : (
        <div className="d-flex flex-column gap-2">
          {walls.map((w) => {
            const otherGroup = w.wall_group_id && w.wall_group_id !== group?.id;
            const mismatch = detail?.rows && (w.rows !== detail.rows || w.columns !== detail.columns || w.orientation !== detail.orientation);
            return (
              <label key={w.id} className="d-flex align-items-center gap-2 p-2" style={{ background: 'var(--pp-bg-secondary)', borderRadius: 'var(--pp-radius-md)', cursor: mismatch ? 'not-allowed' : 'pointer', opacity: mismatch ? 0.6 : 1 }}>
                <Checkbox checked={selected.includes(w.id)} disabled={mismatch} onChange={() => toggle(w.id)} />
                <span style={{ color: 'var(--pp-text-primary)' }}>{w.name}</span>
                <small style={{ color: 'var(--pp-text-secondary)' }}>· {w.rows}×{w.columns}</small>
                {mismatch && <small className="ms-auto" style={{ color: 'var(--pp-warning)' }}>{t('video_wall.groups.wrong_geometry')}</small>}
                {!mismatch && otherGroup && <small className="ms-auto" style={{ color: 'var(--pp-warning)' }}>{t('video_wall.groups.in_other_group')}</small>}
              </label>
            );
          })}
        </div>
      )}
    </Dialog>
  );
}
